import Entity from '../../../core/domain/entity';
import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { DietitianId } from '../../dietitian/domain/dietitian';
import { SharedDocumentId } from '../../document/domain/shared_document/shared_document';
import { PatientId } from '../../patient/domain/patient';
import {
  InvalidTranscriptionTypeException,
  TranscriptionTooBigException,
} from './transcription_exceptions';

export const AUDIO_MAX_SIZE = 200 * 1024 * 1024;

export const AUDIO_MIME_TYPES = [
  'audio/aac',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'audio/x-wav',
  'audio/x-m4a',
];

export interface TranscriptionSegmentProps {
  start: number;
  end: number;
  text: string;
  speaker: string;
}

export interface TranscriptionReportProps {
  code: string;
  label: string;
  value: any;
  inputTokens: number | undefined;
  outputTokens: number | undefined;
}

export interface TranscriptionProps {
  dietitianId: DietitianId;
  patientId: PatientId;
  name: string;
  label: string;
  createdAt?: Date;
  updatedAt?: Date;
  path: string;
  file?: File;
  blob?: Blob;
  size: number;
  mimeType: string;
  audioDurationSeconds?: number | undefined;
  processingDurationSeconds?: number | undefined;
  status: string;
  segments?: TranscriptionSegmentProps[] | undefined;
  segmentsAnonymized?: TranscriptionSegmentProps[] | undefined;
  reports: TranscriptionReportProps[] | undefined;
  sharedDocumentId?: SharedDocumentId | undefined;
  sharedAt?: Date | undefined;
  error?: string | undefined;
}

export class TranscriptionId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): TranscriptionId {
    return new TranscriptionId(id);
  }
}

export class Transcription extends Entity<TranscriptionProps> {
  get transcriptionId(): TranscriptionId {
    return TranscriptionId.create(this.id);
  }

  get dietitianId(): DietitianId {
    return this.props.dietitianId;
  }

  get patientId(): PatientId {
    return this.props.patientId;
  }

  get name(): string {
    return this.props.name;
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get path(): string | undefined {
    return this.props.path;
  }

  get file(): File | undefined {
    return this.props.file;
  }

  set file(value: File | undefined) {
    this.props.file = value;
  }

  get blob() {
    return this.props.blob;
  }

  get size(): number {
    return this.props.size;
  }

  get mimeType(): string {
    return this.props.mimeType;
  }

  get audioDurationSeconds(): number | undefined {
    return this.props.audioDurationSeconds;
  }

  get processingDurationSeconds(): number | undefined {
    return this.props.processingDurationSeconds;
  }

  get status(): string {
    return this.props.status;
  }

  set status(value: string) {
    this.props.status = value;
  }

  get segments(): TranscriptionSegmentProps[] | undefined {
    return this.props.segments;
  }

  get segmentsAnonymized(): TranscriptionSegmentProps[] | undefined {
    return this.props.segmentsAnonymized;
  }

  get reports(): TranscriptionReportProps[] | undefined {
    return this.props.reports;
  }

  set reports(value: TranscriptionReportProps[] | undefined) {
    this.props.reports = value;
  }

  get sharedDocumentId(): SharedDocumentId | undefined {
    return this.props.sharedDocumentId;
  }

  set sharedDocumentId(value: SharedDocumentId | undefined) {
    this.props.sharedDocumentId = value;
  }

  get sharedAt(): Date | undefined {
    return this.props.sharedAt;
  }

  set sharedAt(value: Date | undefined) {
    this.props.sharedAt = value;
  }

  get error(): string | undefined {
    return this.props.error;
  }

  set error(value: string | undefined) {
    this.props.error = value;
  }

  get label(): string {
    return this.props.label;
  }

  set label(value: string) {
    this.props.label = value;
  }

  private constructor(props: TranscriptionProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: TranscriptionProps): Transcription {
    return Transcription.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  public static create(
    props: TranscriptionProps,
    id?: UniqueEntityID,
  ): Transcription {
    if (props.file) {
      if (!Transcription.isTypeAllowed(props.file.type)) {
        throw new InvalidTranscriptionTypeException();
      }
      if (!Transcription.isSizeAllowed(props.file.size)) {
        throw new TranscriptionTooBigException();
      }
    }
    return new Transcription(props, id);
  }

  public static isTypeAllowed = (type: string): boolean =>
    AUDIO_MIME_TYPES.includes(type);

  public static isSizeAllowed = (size: number): boolean =>
    size <= AUDIO_MAX_SIZE;

  public static getDietitianPatientTranscriptionPath(
    dietitianId: string,
    patientId: string,
    transcriptionId: string,
    temp: boolean,
  ): string {
    const suffix = temp ? '_tmp' : '';
    return `recordings/${dietitianId}/${patientId}/${transcriptionId}${suffix}`;
  }
}
